/**
 * useTransferDrawerState Hook
 *
 * Encapsulates all state and actions for the TransferDetailDrawer.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see Sprint 22 - Transferts par Packaging
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { SearchAutocompleteOption } from '@/components/ui';
import {
  TransferStatus,
  canShipTransfer,
  canReceiveTransfer,
  canCancelTransfer,
  canEditTransfer,
  canSubmitTransfer,
  isSourceSite,
  isDestinationSite,
} from '@bstock/shared';
import type {
  TransferDetail,
  ReceiveTransferLineDto,
  CreateTransferLineDto,
  ShipTransferLineDto,
  InventoryPackagingOption,
} from '@bstock/shared';
import {
  useShipTransfer,
  useReceiveTransfer,
  useCancelTransfer,
  useUpdateTransfer,
  useSubmitTransfer,
} from '../../hooks/useTransfers';
import { usePackagingOptions } from '@/hooks';
import { useRawProductsSearch } from '@/features/recipes/hooks/useProductsSearch';
import { usePackagingLineState, useEditLinePackaging } from './usePackagingLineState';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DrawerMode = 'view' | 'edit' | 'ship' | 'receive' | 'cancel';

/** State for an editable line */
export interface EditLineState {
  id: string;
  rawProductId: string;
  rawProductName: string;
  quantity: number;
  unit: string;
  isNew?: boolean;
  // Packaging (Sprint 22)
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
  packagingOptions: InventoryPackagingOption[];
}

export interface ShippedLineState {
  qty: number;
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
}

export interface ReceivedLineState {
  qty: number;
  reason: string;
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
}

export interface UseTransferDrawerStateParams {
  transfer: TransferDetail | undefined;
  currentSiteId: string;
  packagingOptionsMap: Record<string, InventoryPackagingOption[]>;
  hasShipPermission: boolean;
  hasReceivePermission: boolean;
  hasDeletePermission: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTransferDrawerState({
  transfer,
  currentSiteId,
  packagingOptionsMap,
  hasShipPermission,
  hasReceivePermission,
  hasDeletePermission,
  onClose,
}: UseTransferDrawerStateParams) {
  // ---- Mode ----
  const [mode, setMode] = useState<DrawerMode>('view');

  // ---- Cancel state ----
  const [cancelReason, setCancelReason] = useState('');

  // ---- Ship / Receive state ----
  const [shippedQuantities, setShippedQuantities] = useState<Record<string, ShippedLineState>>({});
  const [receivedQuantities, setReceivedQuantities] = useState<Record<string, ReceivedLineState>>({});

  // ---- Backdating state ----
  const [shipDate, setShipDate] = useState<Date | null>(null);
  const [receiveDate, setReceiveDate] = useState<Date | null>(null);

  // ---- Edit state ----
  const [editLines, setEditLines] = useState<EditLineState[]>([]);
  const [editNotes, setEditNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const { data: productOptions = [], isLoading: searchingProducts } = useRawProductsSearch(productSearch);

  // ---- Mutations ----
  const submitMutation = useSubmitTransfer();
  const shipMutation = useShipTransfer();
  const receiveMutation = useReceiveTransfer();
  const cancelMutation = useCancelTransfer();
  const updateMutation = useUpdateTransfer();

  // ---- Edit packaging options (dynamic fetch) ----
  const editRawProductIds = useMemo(
    () => editLines.map((l) => l.rawProductId),
    [editLines],
  );
  const { data: editPackagingOptionsMap } = usePackagingOptions(
    mode === 'edit' ? editRawProductIds : [],
  );

  // Update edit lines with packaging options when data arrives
  useEffect(() => {
    if (!editPackagingOptionsMap || mode !== 'edit') return;
    setEditLines((prev) =>
      prev.map((line) => {
        const options = editPackagingOptionsMap[line.rawProductId] ?? [];
        if (
          options.length !== line.packagingOptions.length ||
          options.some((o, i) => o.packagingId !== line.packagingOptions[i]?.packagingId)
        ) {
          return { ...line, packagingOptions: options };
        }
        return line;
      }),
    );
  }, [editPackagingOptionsMap, mode]);

  // ---- Packaging hooks ----
  const getShipOptions = useCallback(
    (lineId: string) => {
      if (!transfer) return [];
      const line = transfer.lines.find((l) => l.id === lineId);
      if (!line) return [];
      return packagingOptionsMap[line.rawProductId] ?? [];
    },
    [transfer, packagingOptionsMap],
  );

  const getReceiveOptions = useCallback(
    (lineId: string) => {
      if (!transfer) return [];
      const line = transfer.lines.find((l) => l.id === lineId);
      if (!line) return [];
      return packagingOptionsMap[line.rawProductId] ?? [];
    },
    [transfer, packagingOptionsMap],
  );

  const {
    handlePackagingChange: handleShipPackagingChange,
    handlePackagingQtyChange: handleShipPackagingQtyChange,
  } = usePackagingLineState(setShippedQuantities, getShipOptions);

  const {
    handlePackagingChange: handleReceivePackagingChange,
    handlePackagingQtyChange: handleReceivePackagingQtyChange,
  } = usePackagingLineState(setReceivedQuantities, getReceiveOptions);

  const {
    handlePackagingChange: handleEditPackagingChange,
    handlePackagingQtyChange: handleEditPackagingQtyChange,
  } = useEditLinePackaging(setEditLines);

  // ---- Reset ----
  const reset = useCallback(() => {
    setMode('view');
    setEditLines([]);
    setEditNotes('');
    setProductSearch('');
    setCancelReason('');
    setShippedQuantities({});
    setReceivedQuantities({});
    setShipDate(null);
    setReceiveDate(null);
  }, []);

  // ---- Init actions ----
  const initShipping = useCallback(() => {
    if (!transfer) return;
    const initial: Record<string, ShippedLineState> = {};
    transfer.lines.forEach((line) => {
      const options = packagingOptionsMap[line.rawProductId] ?? [];
      if (line.requestPackagingId && line.requestPackagingQty != null) {
        const option = options.find((o) => o.packagingId === line.requestPackagingId);
        initial[line.id] = {
          qty: line.quantityRequested,
          packagingId: line.requestPackagingId,
          packagingQty: String(line.requestPackagingQty),
          conversionFactor: option?.conversionFactor ?? null,
        };
      } else {
        initial[line.id] = {
          qty: line.quantityRequested,
          packagingId: null,
          packagingQty: String(line.quantityRequested),
          conversionFactor: null,
        };
      }
    });
    setShippedQuantities(initial);
    setMode('ship');
  }, [transfer, packagingOptionsMap]);

  const initReceiving = useCallback(() => {
    if (!transfer) return;
    const initial: Record<string, ReceivedLineState> = {};
    transfer.lines.forEach((line) => {
      const options = packagingOptionsMap[line.rawProductId] ?? [];
      if (line.shippedPackagingId && line.shippedPackagingQty != null) {
        const option = options.find((o) => o.packagingId === line.shippedPackagingId);
        initial[line.id] = {
          qty: line.quantityShipped ?? 0,
          reason: '',
          packagingId: line.shippedPackagingId,
          packagingQty: String(line.shippedPackagingQty),
          conversionFactor: option?.conversionFactor ?? null,
        };
      } else {
        initial[line.id] = {
          qty: line.quantityShipped ?? 0,
          reason: '',
          packagingId: null,
          packagingQty: String(line.quantityShipped ?? 0),
          conversionFactor: null,
        };
      }
    });
    setReceivedQuantities(initial);
    setMode('receive');
  }, [transfer, packagingOptionsMap]);

  const initEditing = useCallback(() => {
    if (!transfer) return;
    const lines: EditLineState[] = transfer.lines.map((line) => ({
      id: line.id,
      rawProductId: line.rawProductId,
      rawProductName: line.rawProduct.name,
      quantity: line.quantityRequested,
      unit: line.rawProduct.recipeUnit.symbol,
      packagingId: line.requestPackagingId ?? null,
      packagingQty: line.requestPackagingQty != null ? String(line.requestPackagingQty) : '1',
      conversionFactor: line.requestPackagingId
        ? (packagingOptionsMap[line.rawProductId] ?? []).find(
            (o) => o.packagingId === line.requestPackagingId,
          )?.conversionFactor ?? null
        : null,
      packagingOptions: packagingOptionsMap[line.rawProductId] ?? [],
    }));
    setEditLines(lines);
    setEditNotes(transfer.notes ?? '');
    setMode('edit');
  }, [transfer, packagingOptionsMap]);

  const startCancel = useCallback(() => {
    setMode('cancel');
  }, []);

  // ---- Edit line helpers ----
  const handleAddEditLine = useCallback(
    (option: SearchAutocompleteOption | null) => {
      if (!option) return;
      setEditLines((prev) => {
        if (prev.some((l) => l.rawProductId === option.id)) return prev;
        const unit = (option as unknown as { unit?: string }).unit ?? '';
        return [
          ...prev,
          {
            id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            rawProductId: option.id,
            rawProductName: option.label,
            quantity: 1,
            unit,
            isNew: true,
            packagingId: null,
            packagingQty: '1',
            conversionFactor: null,
            packagingOptions: editPackagingOptionsMap?.[option.id] ?? [],
          },
        ];
      });
      setProductSearch('');
    },
    [editPackagingOptionsMap],
  );

  const handleRemoveEditLine = useCallback((id: string) => {
    setEditLines((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const handleEditQuantityChange = useCallback((id: string, quantity: number) => {
    setEditLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity } : l)));
  }, []);

  // ---- Receive reason helper ----
  const handleReceiveReasonChange = useCallback((lineId: string, reason: string) => {
    setReceivedQuantities((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        reason,
      },
    }));
  }, []);

  // ---- Ship base qty helper ----
  const handleShipBaseQtyChange = useCallback((lineId: string, qty: number) => {
    setShippedQuantities((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        qty,
      },
    }));
  }, []);

  // ---- Receive base qty helper ----
  const handleReceiveBaseQtyChange = useCallback((lineId: string, qty: number) => {
    setReceivedQuantities((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        qty,
      },
    }));
  }, []);

  // ---- Submit actions ----
  const handleSubmit = useCallback(async () => {
    if (!transfer) return;
    await submitMutation.mutateAsync(transfer.id);
    onClose();
  }, [transfer, submitMutation, onClose]);

  const handleShip = useCallback(async () => {
    if (!transfer) return;
    const shipLines: ShipTransferLineDto[] = transfer.lines.map((line) => {
      const shipData = shippedQuantities[line.id];
      if (shipData?.packagingId && shipData.conversionFactor) {
        const pkgQty = parseFloat(shipData.packagingQty) || 0;
        return {
          transferLineId: line.id,
          quantityShipped: pkgQty, // Backend does the conversion (pkgQty * conversionFactor)
          packagingId: shipData.packagingId,
          packagingQty: pkgQty,
          conversionFactor: shipData.conversionFactor,
        };
      }
      return {
        transferLineId: line.id,
        quantityShipped: shipData?.qty ?? line.quantityRequested,
      };
    });
    await shipMutation.mutateAsync({
      id: transfer.id,
      data: {
        lines: shipLines,
        ...(shipDate ? { shipDate: shipDate.toISOString() } : {}),
      },
    });
    onClose();
  }, [transfer, shippedQuantities, shipDate, shipMutation, onClose]);

  const handleReceive = useCallback(async () => {
    if (!transfer) return;
    const lines: ReceiveTransferLineDto[] = transfer.lines.map((line) => {
      const recvData = receivedQuantities[line.id];
      if (recvData?.packagingId && recvData.conversionFactor) {
        const pkgQty = parseFloat(recvData.packagingQty) || 0;
        return {
          transferLineId: line.id,
          quantityReceived: pkgQty, // Backend does the conversion (pkgQty * conversionFactor)
          varianceReason: recvData.reason || undefined,
          packagingId: recvData.packagingId,
          packagingQty: pkgQty,
          conversionFactor: recvData.conversionFactor,
        };
      }
      return {
        transferLineId: line.id,
        quantityReceived: recvData?.qty ?? line.quantityShipped ?? 0,
        varianceReason: recvData?.reason || undefined,
      };
    });
    await receiveMutation.mutateAsync({
      id: transfer.id,
      data: {
        lines,
        ...(receiveDate ? { receiveDate: receiveDate.toISOString() } : {}),
      },
    });
    onClose();
  }, [transfer, receivedQuantities, receiveDate, receiveMutation, onClose]);

  const handleCancel = useCallback(async () => {
    if (!transfer || !cancelReason.trim()) return;
    await cancelMutation.mutateAsync({ id: transfer.id, data: { reason: cancelReason } });
    setMode('view');
    setCancelReason('');
    onClose();
  }, [transfer, cancelReason, cancelMutation, onClose]);

  const handleSaveEdit = useCallback(async () => {
    if (!transfer || editLines.length === 0) return;
    const lines: CreateTransferLineDto[] = editLines.map((l) => {
      if (l.packagingId && l.conversionFactor) {
        const pkgQty = parseFloat(l.packagingQty) || 0;
        return {
          rawProductId: l.rawProductId,
          quantity: pkgQty, // Backend does the conversion (pkgQty * conversionFactor)
          packagingId: l.packagingId,
          packagingQty: pkgQty,
          conversionFactor: l.conversionFactor,
        };
      }
      return {
        rawProductId: l.rawProductId,
        quantity: l.quantity,
      };
    });
    await updateMutation.mutateAsync({
      id: transfer.id,
      data: {
        notes: editNotes || null,
        lines,
      },
    });
    setMode('view');
  }, [transfer, editLines, editNotes, updateMutation]);

  const cancelEditing = useCallback(() => {
    setMode('view');
    setEditLines([]);
    setEditNotes('');
    setProductSearch('');
  }, []);

  const cancelMode = useCallback(() => {
    setMode('view');
  }, []);

  // ---- Permissions ----
  const isSource = transfer ? isSourceSite(transfer, currentSiteId) : false;
  const isDestination = transfer ? isDestinationSite(transfer, currentSiteId) : false;

  const canSubmit = !!(transfer && isDestination && canSubmitTransfer(transfer.status));
  const canShip = !!(transfer && isSource && canShipTransfer(transfer.status) && hasShipPermission);
  const canReceive = !!(transfer && isDestination && canReceiveTransfer(transfer.status) && hasReceivePermission);
  const canCancelPerm = !!(
    transfer &&
    canCancelTransfer(transfer.status) &&
    hasDeletePermission &&
    ((transfer.status === TransferStatus.DRAFT && isDestination) ||
      (transfer.status === TransferStatus.PENDING && isDestination) ||
      (transfer.status === TransferStatus.SHIPPED && isSource))
  );
  const canEdit = !!(transfer && isDestination && canEditTransfer(transfer.status));

  return {
    // Mode
    mode,
    // State
    cancelReason,
    setCancelReason,
    shippedQuantities,
    receivedQuantities,
    editLines,
    editNotes,
    setEditNotes,
    productSearch,
    setProductSearch,
    productOptions,
    searchingProducts,
    // Init actions
    initShipping,
    initReceiving,
    initEditing,
    startCancel,
    // Submit actions
    handleSubmit,
    handleShip,
    handleReceive,
    handleCancel,
    handleSaveEdit,
    cancelEditing,
    cancelMode,
    // Edit line helpers
    handleAddEditLine,
    handleRemoveEditLine,
    handleEditQuantityChange,
    // Edit packaging
    handleEditPackagingChange,
    handleEditPackagingQtyChange,
    // Ship packaging
    handleShipPackagingChange,
    handleShipPackagingQtyChange,
    handleShipBaseQtyChange,
    // Receive packaging
    handleReceivePackagingChange,
    handleReceivePackagingQtyChange,
    handleReceiveBaseQtyChange,
    handleReceiveReasonChange,
    // Backdating
    shipDate,
    setShipDate,
    receiveDate,
    setReceiveDate,
    // Permissions
    isSource,
    isDestination,
    canSubmit,
    canShip,
    canReceive,
    canCancel: canCancelPerm,
    canEdit,
    // Mutation states
    submitMutation,
    shipMutation,
    receiveMutation,
    cancelMutation,
    updateMutation,
    // Reset
    reset,
  };
}
